import { computed, inject } from '@angular/core';
import { BasketItemSource } from '@epicuro-next/models/basket';
import {
  activateConfigStrip,
  ConfigStripActivationSource,
} from '@epicuro-next/state/config-strip-state';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStoreFeature,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Store } from '@ngrx/store';
import { SuggestService } from 'data-recommendation';
import { Product } from 'graphql-api/gql-api-interfaces';
import {
  PanelStyle,
  PanelType,
  RecommendedItemsResponseType,
} from 'models-recommendation';
import { filter, pipe, switchMap, tap } from 'rxjs';

const initialState = {
  result: {
    dataSet: [],
    metadata: {
      provider: '',
      providerid: '',
      processors: [],
    },
  },
  operationId: '',
  hash: '',
  isLoaded: false,
  isLoading: false,
  error: null,
};

interface LoadingStatusState {
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
}

export function setLoading(): Partial<LoadingStatusState> {
  return {
    isLoading: true,
    error: null,
  };
}

export function setLoaded(): Partial<LoadingStatusState> {
  return { isLoaded: true, isLoading: false, error: null };
}

export function setError(error: string): Partial<LoadingStatusState> {
  return { error, isLoaded: true, isLoading: false };
}

export function withSuggestProducts<
  TProductDataSetItem,
  TProviderProperties extends object,
>(panelType: PanelType, panelStyle: PanelStyle) {
  return signalStoreFeature(
    withState<
      RecommendedItemsResponseType<TProductDataSetItem> & LoadingStatusState
    >(initialState),
    withComputed(({ error }) => ({
      hasError: computed(() => !!error()),
    })),
    withMethods(
      (
        state,
        suggestService = inject(SuggestService),
        store = inject(Store),
      ) => ({
        loadSuggestProducts: rxMethod<TProviderProperties>(
          pipe(
            filter(
              () =>
                !state.isLoading() && (!state.isLoaded() || state.hasError()),
            ),
            tap(() => patchState(state, setLoading())),
            switchMap((properties) => {
              return suggestService
                .suggest<TProviderProperties, TProductDataSetItem>({
                  panelType,
                  properties,
                })
                .pipe(
                  tapResponse({
                    next: (response) => {
                      patchState(state, setLoaded());
                      patchState(state, response);
                    },
                    error: (error: { reason: string }) => {
                      patchState(
                        state,
                        setError(error?.reason ?? 'Failed to load'),
                      );
                    },
                  }),
                );
            }),
          ),
        ),
        handleProductClick: (product: Product) => {
          store.dispatch(
            activateConfigStrip({
              index: 1,
              setSourceNeeded: true,
              source: {
                source: ConfigStripActivationSource.Search,
                subSource: BasketItemSource.RECOMMENDATION_PANEL,
                details: {
                  recommendationPanel: {
                    correlationId: state.operationId(),
                    panelName: panelType,
                    panelStyle,
                    providerId: state.result.metadata.providerid(),
                    servingConfig: state.result.metadata.provider(),
                    hash: state.hash(),
                  },
                },
              },
              product,
            }),
          );
        },
      }),
    ),
  );
}
